import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAdminTranslation } from '../../utils/useAdminTranslation';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Globe, Menu, X, Link2, User, LogOut, Shield, Wallet, CreditCard, ShoppingBag, Clock } from 'lucide-react';
import { CartIcon } from './CartIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const { tx } = useAdminTranslation();
  const { user, isAdmin, isWorker, isImpersonated, logout, endImpersonation, token } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch wallet balance
  useEffect(() => {
    const fetchWallet = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/api/wallet`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setWalletBalance(data.balance || 0);
        }
      } catch (error) {
        console.error('Error fetching wallet:', error);
      }
    };
    fetchWallet();
    // Refresh every 30 seconds
    const interval = setInterval(fetchWallet, 30000);
    return () => clearInterval(interval);
  }, [token]);

  // Navigation links removed for cleaner client experience
  const navLinks = [];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href.replace('#', ''));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      {/* Impersonation Banner */}
      {isImpersonated && (
        <div className="bg-amber-500 text-white py-2 px-4 text-center text-sm font-medium sticky top-0 z-[60]">
          <span className="mr-2">👁️</span>
          {tx(`Estás viendo como: ${user?.email || 'Usuario'} (Suplantado por ${user?.impersonated_by})`,
              `Viewing as: ${user?.email || 'User'} (Impersonated by ${user?.impersonated_by})`)
          }
          <button 
            onClick={endImpersonation}
            className="ml-4 px-3 py-1 bg-white text-amber-600 rounded text-xs font-bold hover:bg-amber-50"
          >
            {tx('Terminar Suplantación', 'End Impersonation')}
          </button>
        </div>
      )}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img 
              src="/logo_purolink.webp" 
              alt="Purolink" 
              className="h-14 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`text-sm font-medium transition-colors animated-underline ${
                  isActive(link.href)
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.label}
              </a>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                className={`text-sm font-medium transition-colors animated-underline flex items-center gap-1 ${
                  location.pathname === '/admin'
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Shield className="h-4 w-4" />
                Admin
              </Link>
            )}
            {isWorker && (
              <Link
                to="/time-tracking"
                className={`text-sm font-medium transition-colors animated-underline flex items-center gap-1 ${
                  location.pathname === '/time-tracking'
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Clock className="h-4 w-4" />
                {tx('Mi Fichaje', 'Time Tracking')}
              </Link>
            )}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Wallet Balance - Always Visible */}
            {user && (
              <Link to="/wallet" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors">
                <Wallet className="h-4 w-4 text-primary" />
                <span className={`font-bold ${walletBalance > 0 ? 'text-primary' : 'text-red-600'}`}>
                  {walletBalance.toFixed(2)} €
                </span>
                {walletBalance === 0 && (
                  <CreditCard className="h-4 w-4 text-red-500 animate-pulse" />
                )}
              </Link>
            )}

            {/* Cart Icon */}
            {user && <CartIcon />}
            
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline uppercase">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={() => setLanguage('es')}
                  className={language === 'es' ? 'bg-primary/10 text-primary' : ''}
                >
                  <span className="mr-2">🇪🇸</span>
                  Español
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('en')}
                  className={language === 'en' ? 'bg-primary/10 text-primary' : ''}
                >
                  <span className="mr-2">🇬🇧</span>
                  English
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('de')}
                  className={language === 'de' ? 'bg-primary/10 text-primary' : ''}
                >
                  <span className="mr-2">🇩🇪</span>
                  Deutsch
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('fr')}
                  className={language === 'fr' ? 'bg-primary/10 text-primary' : ''}
                >
                  <span className="mr-2">🇫🇷</span>
                  Français
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('it')}
                  className={language === 'it' ? 'bg-primary/10 text-primary' : ''}
                >
                  <span className="mr-2">🇮🇹</span>
                  Italiano
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('pt')}
                  className={language === 'pt' ? 'bg-primary/10 text-primary' : ''}
                >
                  <span className="mr-2">🇧🇷</span>
                  Português
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('zh')}
                  className={language === 'zh' ? 'bg-primary/10 text-primary' : ''}
                >
                  <span className="mr-2">🇨🇳</span>
                  中文
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('ar')}
                  className={language === 'ar' ? 'bg-primary/10 text-primary' : ''}
                >
                  <span className="mr-2">🇸🇦</span>
                  العربية
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 pl-2">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium max-w-[100px] truncate">
                      {user.name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/my-account')}>
                    <User className="mr-2 h-4 w-4" />
                    {tx('Mi Cuenta', 'My Account')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/wallet')}>
                    <Wallet className="mr-2 h-4 w-4" />
                    {tx('Mi Monedero', 'My Wallet')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-orders')}>
                    <ShoppingBag className="mr-2 h-4 w-4" />
                    {tx('Mis Pedidos', 'My Orders')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-invoices')}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    {tx('Mis Facturas', 'My Invoices')}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Shield className="mr-2 h-4 w-4" />
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  {isWorker && (
                    <DropdownMenuItem onClick={() => navigate('/time-tracking')}>
                      <Clock className="mr-2 h-4 w-4" />
                      {tx('Mi Fichaje', 'Time Tracking')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    {tx('Cerrar Sesión', 'Sign Out')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border py-4 animate-fade-in-down">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {link.label}
                </a>
              ))}
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    location.pathname === '/admin'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  Admin Panel
                </Link>
              )}
              {isWorker && (
                <Link
                  to="/time-tracking"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    location.pathname === '/time-tracking'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  {tx('Mi Fichaje', 'Time Tracking')}
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
    </>
  );
};
